import type { FundoDoHero } from '@plane/shared';

import { prefereMenosMovimento } from '@/hooks/use-revelar-ao-rolar';
import { apiBaseUrl } from '@/services/http-client';

/**
 * Fundo do banner: verde sólido, imagem ou vídeo.
 *
 * O fundo sólido é a base da seção e continua embaixo dos outros dois — imagem
 * e vídeo **cobrem**, não substituem. É o que garante que uma falha de rede, um
 * autoplay recusado ou uma imagem que não carregou deixem o banner com o verde
 * da marca, e não com um retângulo preto.
 */
export function FundoDoHero({ fundo }: { fundo: FundoDoHero }) {
  if (fundo.tipo === 'solido') return null;

  if (fundo.tipo === 'imagem') {
    return (
      <img
        // Decorativa: o que a seção comunica está no título e no parágrafo, e
        // descrever a textura do fundo só atrapalharia quem usa leitor de tela.
        alt=""
        aria-hidden="true"
        src={`${apiBaseUrl}/api/v1/media/${fundo.mediaId}`}
        className="pointer-events-none absolute inset-0 -z-20 h-full w-full object-cover"
      />
    );
  }

  return <FundoDeVideo youtubeId={fundo.youtubeId} />;
}

/**
 * Vídeo do YouTube em laço.
 *
 * Escolhas que valem registro, porque nenhuma é óbvia:
 *
 * **Não monta para quem pediu menos movimento.** Vídeo em laço atrás de texto é
 * exatamente o que essa preferência existe para desligar.
 *
 * **`youtube-nocookie.com`.** O domínio comum grava identificador de rastreio em
 * quem só abriu a página.
 *
 * **Fora da ordem de tabulação e do leitor de tela.** É decoração: não há
 * conteúdo ali que alguém precise alcançar, e um `<iframe>` focável no meio do
 * caminho só atrapalharia quem navega por teclado.
 *
 * O recorte 16:9 é esticado até cobrir a seção pelos dois lados: `100vw` de
 * largura com altura proporcional, ou `177.78vh` de largura quando a tela é
 * mais alta que larga. É o que evita tarja.
 */
function FundoDeVideo({ youtubeId }: { youtubeId: string }) {
  if (prefereMenosMovimento()) return null;

  const parametros = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    controls: '0',
    loop: '1',
    // Sem `playlist` apontando para o próprio vídeo, o `loop` não faz nada.
    playlist: youtubeId,
    playsinline: '1',
    rel: '0',
    disablekb: '1',
    iv_load_policy: '3',
  });

  return (
    <span aria-hidden="true" className="pointer-events-none absolute inset-0 -z-20 overflow-hidden">
      <iframe
        title=""
        tabIndex={-1}
        allow="autoplay; encrypted-media"
        referrerPolicy="strict-origin-when-cross-origin"
        src={`https://www.youtube-nocookie.com/embed/${youtubeId}?${parametros.toString()}`}
        className="absolute top-1/2 left-1/2 h-[56.25vw] min-h-full w-[100vw] min-w-[177.78vh] -translate-x-1/2 -translate-y-1/2 border-0"
      />
    </span>
  );
}
